/**
 * Calendar event reads for any Supabase client: the web passes its singleton,
 * the React Native app its own. Recurring masters come back as rows; callers
 * expand them with expandRecurringMasters for the window they show.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import type { CalendarEvent } from '../types/calendar';
import type { Database as SupabaseDatabase } from '../types/generated/database';

export type CalendarEventRow = SupabaseDatabase['public']['Tables']['calendar_events']['Row'] & {
  is_flexible_task?: boolean | null;
  reminders?: number[] | null;
};

export function toCalendarEvent(row: CalendarEventRow): CalendarEvent {
  return {
    ...row,
    reminders: row.reminders ?? [],
    description: row.description ?? undefined,
    location: row.location ?? undefined,
    task_id: row.task_id ?? undefined,
    is_flexible_task: row.is_flexible_task === true,
    google_event_id: row.google_event_id ?? undefined,
    rrule: row.rrule ?? undefined,
    master_event_id: row.master_event_id ?? undefined,
    original_start_time: row.original_start_time ?? undefined,
    exdate: row.exdate ?? undefined,
    color: row.color as CalendarEvent['color'],
  };
}

// Typed loosely: the web and native apps each bring their own supabase-js.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function createCalendarEventReads(client: SupabaseClient<any>) {
  async function getEvents(userId: string, from: Date, to: Date): Promise<CalendarEvent[]> {
    // Two queries to handle recurring masters that started before the display window:
    // 1. Non-recurring events (and override rows) that start within the range.
    // 2. All master rows with an rrule that started at any time up to the range end —
    //    the client-side expander will filter instances to the visible window.
    const [inRange, masters] = await Promise.all([
      client
        .from('calendar_events')
        .select('*')
        .eq('user_id', userId)
        .gte('start_time', from.toISOString())
        .lte('start_time', to.toISOString()),
      client
        .from('calendar_events')
        .select('*')
        .eq('user_id', userId)
        .not('rrule', 'is', null)
        .lte('start_time', to.toISOString()),
    ]);

    if (inRange.error) throw inRange.error;
    if (masters.error) throw masters.error;

    // Deduplicate: a master whose start_time falls within the range appears in both sets.
    const seen = new Set<string>();
    const merged: CalendarEvent[] = [];
    for (const row of [...(inRange.data ?? []), ...(masters.data ?? [])]) {
      if (!seen.has(row.id)) {
        seen.add(row.id);
        merged.push(toCalendarEvent(row));
      }
    }

    return merged.sort((a, b) => a.start_time.localeCompare(b.start_time));
  }

  return { getEvents };
}
